/**
 * Transient circular-junction turntable rotation.
 * Kept out of Redux so RAF ticks don't flood the store.
 *
 * Phases:
 *  - `waiting`: dedicated pre-turn hold; disk and heading stay put.
 *  - `aligning`: disk swings to the entry axis (approach) or to the exit (boarded).
 *  - `rotating`: OHT is boarded at the hub and turns with the disk to the exit.
 */
export type JunctionTurntablePhase = 'waiting' | 'aligning' | 'rotating';

export type JunctionTurntableAnim = {
  phase: JunctionTurntablePhase;
  /** Disk angle (element-local deg) when the reservation was made. */
  alignFromDeg: number;
  /** Disk angle (element-local deg) lined up with the entry port axis. */
  alignToDeg: number;
  /** OHT heading (element-local deg) while driving in. */
  headingInDeg: number;
  /** Signed turn applied to both disk and OHT heading during `rotating`. */
  deltaDeg: number;
  /**
   * Optional signed heading sweep for boarded holds that animate the bar via
   * `aligning` (`deltaDeg ≈ 0`) while the vehicle turns toward the exit.
   */
  headingDeltaDeg?: number;
  /** Exit bar to apply after the dedicated wait finishes. */
  pendingAlignToDeg?: number;
  /** Heading sweep to apply after the dedicated wait finishes. */
  pendingHeadingDeltaDeg?: number;
  /** 0..1 within the current phase. */
  progress: number;
  startedAt: number;
  ohtId: string;
  fromPort: number;
  toPort: number;
  /** True once the OHT has reached the hub and rides the disk. */
  boarded: boolean;
  exitEdgeIndex: number;
  exitDir: 1 | -1;
  /**
   * Dedicated wait duration (sim ms). Used only while `phase === 'waiting'`.
   */
  minHoldMs?: number;
  /** Total sim-seconds to rewind the OHT clock after wait + rotate. */
  holdDelaySec?: number;
};

type Listener = () => void;

/** Shortest signed delta from `from` to `to` in (−180, 180]. */
export function shortestAngleDeltaDeg(fromDeg: number, toDeg: number): number {
  let d = ((toDeg - fromDeg) % 360 + 360) % 360;
  if (d > 180) d -= 360;
  return d;
}

/** Interpolate along the shortest arc. */
export function lerpAngleShortestDeg(fromDeg: number, toDeg: number, t: number): number {
  return fromDeg + shortestAngleDeltaDeg(fromDeg, toDeg) * Math.max(0, Math.min(1, t));
}

/**
 * Undirected diameter alignment: a bar at `a` is the same physical rail as
 * one at `b` when they differ by 0° or 180°.
 */
export function turntableAnglesAligned(aDeg: number, bDeg: number, tolDeg = 5): boolean {
  const d = Math.abs(shortestAngleDeltaDeg(aDeg, bDeg));
  return Math.min(d, Math.abs(180 - d)) <= tolDeg;
}

class JunctionTurntableStore {
  private anims = new Map<string, JunctionTurntableAnim>();
  private cooldowns = new Map<string, number>();
  private ohtCooldowns = new Map<string, number>();
  /** OHTs that just finished a rotation, locked until they leave the hub. */
  private parked = new Map<string, string>();
  /** Junction IDs currently claimed/occupied by an OHT (junctionId -> ohtId). */
  private claims = new Map<string, string>();
  private listeners = new Set<Listener>();
  /** Quantized hub angles — skip React notify when display angle unchanged. */
  private lastEmitSig = '';

  getAnim(id: string): JunctionTurntableAnim | undefined {
    return this.anims.get(id);
  }

  getAll(): ReadonlyMap<string, JunctionTurntableAnim> {
    return this.anims;
  }

  /** Live disk angle for drawing; falls back to the settled Redux angle. */
  getHubAngle(id: string, settledAngleDeg: number): number {
    const anim = this.anims.get(id);
    if (!anim) return settledAngleDeg;
    // Smoothstep so disk accel/decel instead of linear jerk.
    const p = Math.max(0, Math.min(1, anim.progress));
    const eased = p * p * (3 - 2 * p);
    if (anim.phase === 'waiting') {
      return anim.alignFromDeg;
    }
    if (anim.phase === 'aligning') {
      return lerpAngleShortestDeg(anim.alignFromDeg, anim.alignToDeg, eased);
    }
    return anim.alignToDeg + anim.deltaDeg * eased;
  }

  /** Disk angle once the whole ride finishes. */
  getFinalAngle(anim: JunctionTurntableAnim): number {
    return anim.alignToDeg + anim.deltaDeg;
  }

  isAnimating(id: string): boolean {
    return this.anims.has(id);
  }

  isReservedBy(id: string, ohtId: string): boolean {
    return this.anims.get(id)?.ohtId === ohtId;
  }

  isCoolingDown(id: string, now = performance.now()): boolean {
    const until = this.cooldowns.get(id);
    return until != null && until > now;
  }

  isOhtCoolingDown(ohtId: string, now = performance.now()): boolean {
    const until = this.ohtCooldowns.get(ohtId);
    return until != null && until > now;
  }

  getParkedJunction(ohtId: string): string | undefined {
    return this.parked.get(ohtId);
  }

  releaseParked(ohtId: string) {
    if (this.parked.delete(ohtId)) this.emit();
  }

  /** OHT ids frozen on a disk. Approaching OHTs keep driving, so boarded only. */
  getBusyOhtIds(): Set<string> {
    const ids = new Set<string>();
    for (const anim of this.anims.values()) {
      if (anim.boarded) ids.add(anim.ohtId);
    }
    return ids;
  }

  /** True only once the OHT is physically riding the disk. */
  isOhtOnTurntable(ohtId: string): boolean {
    for (const anim of this.anims.values()) {
      if (anim.boarded && anim.ohtId === ohtId) return true;
    }
    return false;
  }

  /** True when any disk is reserved for this OHT, boarded or still approaching. */
  isOhtReserved(ohtId: string): boolean {
    for (const anim of this.anims.values()) {
      if (anim.ohtId === ohtId) return true;
    }
    return false;
  }

  /** Mark the OHT as having reached the hub. Returns false if it can't board yet. */
  markBoarded(id: string, ohtId: string): boolean {
    const anim = this.anims.get(id);
    if (!anim || anim.ohtId !== ohtId) return false;
    if (!anim.boarded) {
      anim.boarded = true;
      this.emit();
    }
    return true;
  }

  claimJunction(junctionId: string, ohtId: string): boolean {
    const current = this.claims.get(junctionId);
    if (!current || current === ohtId) {
      this.claims.set(junctionId, ohtId);
      return true;
    }
    return false;
  }

  releaseJunction(junctionId: string, ohtId: string) {
    if (this.claims.get(junctionId) === ohtId) {
      this.claims.delete(junctionId);
    }
  }

  isJunctionClaimedByOther(junctionId: string, ohtId: string): boolean {
    const current = this.claims.get(junctionId);
    return current != null && current !== ohtId;
  }

  getJunctionClaim(junctionId: string): string | undefined {
    return this.claims.get(junctionId);
  }

  start(params: {
    id: string;
    phase?: JunctionTurntablePhase;
    alignFromDeg: number;
    alignToDeg: number;
    headingInDeg: number;
    deltaDeg: number;
    headingDeltaDeg?: number;
    fromPort: number;
    toPort: number;
    exitEdgeIndex: number;
    exitDir: 1 | -1;
    ohtId: string;
    boarded?: boolean;
    now?: number;
    force?: boolean;
    minHoldMs?: number;
    pendingAlignToDeg?: number;
    pendingHeadingDeltaDeg?: number;
    holdDelaySec?: number;
  }) {
    const { id, ohtId, now = performance.now(), force = false } = params;

    if (!force) {
      if (this.anims.has(id)) return;
      if (this.isCoolingDown(id, now)) return;
      if (this.isOhtCoolingDown(ohtId, now)) return;
    }

    this.anims.set(id, {
      phase: params.phase ?? 'aligning',
      alignFromDeg: params.alignFromDeg,
      alignToDeg: params.alignToDeg,
      headingInDeg: params.headingInDeg,
      deltaDeg: params.deltaDeg,
      headingDeltaDeg: params.headingDeltaDeg,
      progress: 0,
      startedAt: now,
      ohtId,
      fromPort: params.fromPort,
      toPort: params.toPort,
      boarded: params.boarded ?? false,
      exitEdgeIndex: params.exitEdgeIndex,
      exitDir: params.exitDir,
      minHoldMs: params.minHoldMs,
      pendingAlignToDeg: params.pendingAlignToDeg,
      pendingHeadingDeltaDeg: params.pendingHeadingDeltaDeg,
      holdDelaySec: params.holdDelaySec,
    });
    this.emit();
  }

  /**
   * Advance phases. `aligning` runs on its own; `rotating` only progresses once
   * the OHT has boarded, so the disk never turns out from under it.
   *
   * Entry-only alignments (`deltaDeg ≈ 0`, not boarded) complete when aligning
   * reaches 1 so the disk settles on the entry axis before the OHT drives in.
   * Returns junction ids whose ride (or entry align) is finished.
   */
  tick(
    now: number,
    durationMs: (anim: JunctionTurntableAnim) => number
  ): string[] {
    const completed: string[] = [];
    let changed = false;

    for (const [id, anim] of this.anims) {
      if (anim.phase === 'waiting') {
        const dur = Math.max(1, durationMs(anim));
        if (now < anim.startedAt) {
          anim.startedAt = now;
          anim.progress = 0;
          changed = true;
          continue;
        }
        const next = Math.min(1, Math.max(0, (now - anim.startedAt) / dur));
        if (next !== anim.progress) {
          anim.progress = next;
          changed = true;
        }
        if (anim.progress < 1) continue;
        // Wait is over — start the disk/heading animation. Carry leftover time
        // so a large clock jump cannot stall between phases.
        const leftover = Math.max(0, now - anim.startedAt - dur);
        anim.phase = 'aligning';
        anim.alignFromDeg = anim.alignToDeg;
        if (typeof anim.pendingAlignToDeg === 'number') {
          anim.alignToDeg = anim.pendingAlignToDeg;
        }
        if (typeof anim.pendingHeadingDeltaDeg === 'number') {
          anim.headingDeltaDeg = anim.pendingHeadingDeltaDeg;
        }
        anim.deltaDeg = 0;
        anim.minHoldMs = undefined;
        anim.progress = 0;
        anim.startedAt = now - leftover;
        changed = true;
      }

      if (anim.phase === 'aligning') {
        const dur = Math.max(1, durationMs(anim));
        if (now < anim.startedAt) {
          anim.startedAt = now;
          anim.progress = 0;
          changed = true;
          continue;
        }
        const next = Math.min(1, Math.max(0, (now - anim.startedAt) / dur));
        if (next !== anim.progress) {
          anim.progress = next;
          changed = true;
        }
        if (anim.progress >= 1 && anim.boarded && Math.abs(anim.deltaDeg) < 0.5) {
          // Boarded align-only hold (disk now faces the exit axis) — done.
          completed.push(id);
          continue;
        } else if (anim.progress >= 1 && anim.boarded) {
          const leftover = Math.max(0, now - anim.startedAt - dur);
          anim.phase = 'rotating';
          anim.progress = 0;
          anim.startedAt = now - leftover;
          changed = true;
        } else if (anim.progress >= 1 && !anim.boarded && Math.abs(anim.deltaDeg) < 0.5) {
          // Entry-only pre-align finished — settle without a hub ride.
          completed.push(id);
          continue;
        } else {
          continue;
        }
      }

      if (!anim.boarded) continue;

      const dur = Math.max(1, durationMs(anim));
      if (now < anim.startedAt) {
        anim.startedAt = now;
        anim.progress = 0;
        changed = true;
        continue;
      }
      const next = Math.min(1, Math.max(0, (now - anim.startedAt) / dur));
      if (next !== anim.progress) {
        anim.progress = next;
        changed = true;
      }
      if (anim.progress >= 1) completed.push(id);
    }

    if (changed) this.emitIfDisplayChanged();
    return completed;
  }

  clear(id: string) {
    if (!this.anims.has(id) && !this.claims.has(id)) return;
    this.anims.delete(id);
    this.claims.delete(id);
    this.cooldowns.delete(id);
    this.emit();
  }

  markSettled(id: string, now = performance.now(), cooldownMs = 700) {
    const prev = this.anims.get(id);
    if (!prev) return;
    this.cooldowns.set(id, now + cooldownMs);
    this.ohtCooldowns.set(prev.ohtId, now + cooldownMs);
    this.parked.set(prev.ohtId, id);
    this.anims.delete(id);
    this.emit();
  }

  /** Test helper — wipe all transient turntable state. */
  reset() {
    this.anims.clear();
    this.cooldowns.clear();
    this.ohtCooldowns.clear();
    this.parked.clear();
    this.claims.clear();
    this.emit();
  }

  subscribe(listener: Listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit() {
    this.lastEmitSig = this.displaySig();
    this.listeners.forEach((l) => l());
  }

  private displaySig(): string {
    const parts: string[] = [];
    for (const [id, anim] of this.anims) {
      // Match CanvasElement 0.5° quantize so emits align with visible updates.
      const a = Math.round(this.getHubAngle(id, anim.alignFromDeg) * 2) / 2;
      parts.push(`${id}:${a}:${anim.phase}:${anim.boarded ? 1 : 0}`);
    }
    for (const [id, oht] of this.claims) {
      parts.push(`c:${id}:${oht}`);
    }
    return parts.join('|');
  }

  private emitIfDisplayChanged() {
    const sig = this.displaySig();
    if (sig === this.lastEmitSig) return;
    this.lastEmitSig = sig;
    this.listeners.forEach((l) => l());
  }
}

export const junctionTurntableStore = new JunctionTurntableStore();
